/**
 * Phone number utilities for Australian phone numbers
 * Converts various input formats to E.164 format for Twilio compatibility
 */

/**
 * Formats an Australian phone number to E.164 format (+61XXXXXXXXX)
 *
 * Accepts various input formats:
 * - 0434141631 → +61434141631
 * - 0434 141 631 → +61434141631
 * - 04 3414 1631 → +61434141631
 * - +61434141631 → +61434141631
 * - 61434141631 → +61434141631
 * - 434141631 → +61434141631
 *
 * @param phone - The phone number in any common Australian format
 * @returns The phone number in E.164 format, or null if invalid
 */
export const formatAustralianPhoneToE164 = (phone: string): string | null => {
  if (!phone) return null;

  // Remove all non-digit characters except leading +
  let cleaned = phone.replace(/[^\d+]/g, '');

  // If it starts with +, keep it and remove non-digits after
  if (cleaned.startsWith('+')) {
    cleaned = '+' + cleaned.slice(1).replace(/\D/g, '');
  }

  // Already in E.164 format with +61
  if (cleaned.startsWith('+61')) {
    // Validate length: +61 + 9 digits = 12 characters
    if (cleaned.length === 12) {
      return cleaned;
    }
    return null;
  }

  // Starts with 61 (country code without +)
  if (cleaned.startsWith('61') && cleaned.length === 11) {
    return '+' + cleaned;
  }

  // Starts with 0 (Australian local format)
  if (cleaned.startsWith('0') && cleaned.length === 10) {
    // Replace leading 0 with +61
    return '+61' + cleaned.slice(1);
  }

  // Just 9 digits (missing leading 0)
  if (cleaned.length === 9 && !cleaned.startsWith('0')) {
    return '+61' + cleaned;
  }

  // Invalid format
  return null;
};

/**
 * Formats a phone number for display (user-friendly format)
 *
 * @param phone - The phone number (any format)
 * @returns Formatted display string like "0434 141 631" or the original if invalid
 */
export const formatPhoneForDisplay = (phone: string): string => {
  if (!phone) return '';

  // First convert to E.164
  const e164 = formatAustralianPhoneToE164(phone);

  if (!e164) {
    // Return original if can't parse
    return phone;
  }

  // Convert +61XXXXXXXXX to 0XXX XXX XXX
  const localNumber = '0' + e164.slice(3); // Remove +61, add 0

  // Format as 0XXX XXX XXX
  return localNumber.replace(/(\d{4})(\d{3})(\d{3})/, '$1 $2 $3');
};

/**
 * Validates if a string is a valid Australian mobile number
 *
 * @param phone - The phone number to validate
 * @returns true if valid Australian mobile number
 */
export const isValidAustralianMobile = (phone: string): boolean => {
  const e164 = formatAustralianPhoneToE164(phone);

  if (!e164) return false;

  // Australian mobile numbers start with +614
  return e164.startsWith('+614');
};
